import type { ReactNode } from 'react';

/**
 * Esqueleto de una sección del shell — ADR-2026-08-03-todas-las-secciones-cargan-igual.
 *
 * La regla es la misma que estrenó la home
 * (ADR-2026-08-03-home-aparece-entera-o-skeleton): mientras falte algo, bloques
 * grises; cuando está todo, la pantalla entra de una vez con una transición corta.
 * Acá vive la parte compartida para no reescribirla en cada pantalla.
 *
 * Lo que NO puede faltar en ningún esqueleto de sección, y por eso está acá y no en
 * cada pantalla: el `<h1>`. Los 40px de aire de arriba del título los pone una regla
 * del shell que selecciona por `:has(> h1)` (`.shell-seccion` en `index.css`,
 * ADR-2026-07-31-titulos-seccion-tamano-saludo-mismo). Un esqueleto sin `<h1>` no la
 * matchea y arranca 40px más arriba que el contenido real — que es exactamente el
 * salto que este componente existe para evitar
 * (ADR-2026-08-03-el-esqueleto-mide-lo-mismo-que-la-home).
 */

/** Bloque gris. Mismo tratamiento en todas las pantallas. */
export const BLOQUE_ESQUELETO = 'rounded-xl bg-black/[0.06] animate-pulse';

/**
 * La clase que hace entrar al contenido real cuando termina de cargar. Se aplica
 * sobre la raíz que YA tiene la pantalla, nunca sobre un `<div>` nuevo que la
 * envuelva: dos reglas del shell (`.shell-seccion > :not(.fixed)` y la del `:has(>
 * h1)`) alcanzan sólo al hijo DIRECTO del wrapper, y un contenedor de más las deja
 * apuntando al lugar equivocado.
 */
export const APARECE = 'aparece-tras-esqueleto';

/**
 * Clases para la raíz de la pantalla según el estado. Mientras carga va
 * `display: none` y NO desmontada: los hijos tienen que seguir montados para poder
 * hacer sus fetch y avisar que terminaron. Si el contenido se desmontara, las
 * pantallas cuyos datos los cargan sus hijos no llegarían nunca a "listo".
 */
export const claseContenido = (listo: boolean): string =>
  listo ? APARECE : 'hidden';

export function EsqueletoSeccion({
  /**
   * Clases de la RAÍZ de la pantalla que se está imitando — en la práctica, su
   * padding y su `max-w-content mx-auto`. Hay que pasarlas: dentro del shell no
   * hacen nada (`.shell-seccion > :not(.fixed)` las anula, igual que a las de la
   * pantalla real), pero fuera —mobile, `DashboardLayout`— son las que evitan que el
   * esqueleto quede pegado a los bordes mientras el contenido real no lo está.
   */
  className = '',
  /** Ancho de la barra del título. Aproxima el largo del texto real. */
  anchoTitulo = 'w-[260px]',
  /**
   * Clases de la FILA del título, **copiadas enteras** de la pantalla que se está
   * imitando —incluido el `display`—. Van completas y no como agregado a un default
   * porque no todas las filas son iguales: las de `Creaciones` y `Mi marca` son un
   * flex de dos columnas y la de `/plantillas` es un bloque con el breadcrumb arriba
   * del título. El alto y el padding de arriba los pone el shell.
   */
  claseFilaTitulo = 'hidden sm:flex items-center justify-between mb-5',
  /** Lo que va ARRIBA del título DENTRO de la misma fila — el breadcrumb de
   *  `/plantillas`, que vive adentro del mismo `<header>` que el `<h1>`. */
  antesDelTitulo,
  /**
   * Lo que va arriba de la fila del título, como HERMANO — el breadcrumb del estudio
   * de video, que es un elemento suelto antes de la fila.
   *
   * La distinción no es cosmética: los 40px de aire del shell se los lleva la fila
   * que contiene el `<h1>`, así que un breadcrumb ADENTRO queda arriba de esos 40px y
   * uno AFUERA queda abajo. Cada pantalla tiene que usar el mismo slot que su
   * markup real o el esqueleto y el contenido no coinciden.
   */
  antesDeLaFila,
  children,
}: {
  className?: string;
  anchoTitulo?: string;
  claseFilaTitulo?: string;
  antesDelTitulo?: ReactNode;
  antesDeLaFila?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div aria-hidden="true" className={className}>
      {antesDeLaFila}
      {/* El `hidden sm:flex` del default es el de los títulos de desktop: abajo de
          `sm` el título lo pone `MobilePageTitle`, que es otro bloque. */}
      <div className={claseFilaTitulo}>
        {antesDelTitulo}
        <h1 className={`h-[38px] ${anchoTitulo} ${BLOQUE_ESQUELETO}`} />
      </div>
      {children}
    </div>
  );
}

export default EsqueletoSeccion;
